import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import api from '../config/axiosConfig';
import { toPersianDigits, toEnglishDigits } from '../utils/convertNumbers';

const AutocompleteWrapper = styled('div')(({ theme }) => ({
    '& .MuiFormControl-root': {
        width: '100%',
    },
    '& .MuiAutocomplete-root': {
        width: '100%',
    },
    // No notch/legend overrides: MUI's own fieldset rule already right-aligns
    // the notch under RTL. A local `textAlign: 'right'` here is flipped to
    // `left` by stylis-plugin-rtl and pushes the notch to the far left.
}));

/**
 * Searchable Autocomplete Component with async search
 * 
 * @param {string} apiUrl - The API endpoint to search (e.g., "/accounts/api/v1/profile/students/")
 * @param {function} getOptionLabel - Function to format the display label
 * @param {function} getOptionValue - Function to get the option value
 * @param {string} label - Label for the input field
 * @param {any} value - Currently selected value
 * @param {function} onChange - Callback when selection changes (receives value and full option object)
 * @param {object} params - Additional query parameters to send with the API request
 * @param {string} placeholder - Placeholder text
 * @param {boolean} disabled - Whether the field is disabled
 * @param {boolean} required - Whether the field is required
 * @param {string} error - Error message to display
 * @param {string} size - Size of the input field ('small', 'medium')
 * @param {object} sx - Additional sx styling for the text field
 */
const SearchableAutocomplete = ({
    apiUrl,
    getOptionLabel,
    getOptionValue,
    label,
    value,
    onChange,
    initialOption = null,
    params = {},
    placeholder = 'جستجو...',
    disabled = false,
    required = false,
    error = null,
    size = 'medium',
    sx: additionalSx = {},
}) => {
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showLoading, setShowLoading] = useState(false); // Delayed loading indicator
    const [inputValue, setInputValue] = useState('');
    const [selectedOption, setSelectedOption] = useState(initialOption);
    const [lastFetchedSearch, setLastFetchedSearch] = useState('');

    // Seed initial option if provided and value matches
    // This effect runs whenever initialOption or value changes
    useEffect(() => {
        if (initialOption) {
            const optionValue = getOptionValue(initialOption);
            
            // Check if we should use initialOption:
            // 1. If value matches and we don't have a selected option yet
            // 2. If value matches and the selected option doesn't match
            if (optionValue == value) { // Use == for type coercion (string vs number)
                const currentOptionValue = selectedOption ? getOptionValue(selectedOption) : null;
                if (currentOptionValue != optionValue) {
                    setSelectedOption(initialOption);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialOption, value]);

    // Find the selected option when value changes
    // But prioritize initialOption if it matches the value
    useEffect(() => {
        // If we have initialOption and it matches the value, use it (highest priority)
        if (initialOption && value) {
            const optionValue = getOptionValue(initialOption);
            if (optionValue == value) { // Use == for type coercion
                const currentOptionValue = selectedOption ? getOptionValue(selectedOption) : null;
                if (currentOptionValue != optionValue) {
                    setSelectedOption(initialOption);
                    return;
                }
            }
        }
        
        // Otherwise, try to find in options list
        if (value && options.length > 0) {
            const found = options.find(opt => getOptionValue(opt) === value || getOptionValue(opt) == value);
            if (found) {
                setSelectedOption(found);
            }
        } else if (!value) {
            // Only clear if we don't have a matching initialOption
            if (!initialOption || !value || getOptionValue(initialOption) != value) {
                setSelectedOption(null);
            }
        }
    }, [value, options, getOptionValue, initialOption, selectedOption]);

    // Fetch options when input changes or dropdown opens
    useEffect(() => {
        let active = true;
        let searchTimeoutId;
        let loadingTimeoutId;

        // Only fetch when dropdown is open
        if (!open) {
            return () => {
                active = false;
            };
        }

        // Convert Persian digits to English for search
        const searchTerm = toEnglishDigits(inputValue).trim();
        
        // Skip if we already fetched this exact search term
        // But always fetch when dropdown first opens (empty search term)
        if (searchTerm === lastFetchedSearch && searchTerm !== '') {
            return () => {
                active = false;
            };
        }

        const fetchOptions = async () => {
            setLoading(true);
            
            // Only show loading spinner if request takes longer than 500ms
            loadingTimeoutId = setTimeout(() => {
                if (active) {
                    setShowLoading(true);
                }
            }, 500);

            try {
                // Add cache-busting timestamp to force fresh data
                const cacheBuster = Date.now();
                const response = await api.get(apiUrl, {
                    params: {
                        ...params,
                        search: searchTerm || undefined,
                        page: 1,
                        page_size: 50,
                        _t: cacheBuster, // Cache buster
                    },
                });

                if (active) {
                    const results = response.data?.results || [];
                    setOptions(results);
                    setLastFetchedSearch(searchTerm);
                }
            } catch (error) {
                console.error('Error fetching options:', error);
                if (active) {
                    setOptions([]);
                }
            } finally {
                if (active) {
                    clearTimeout(loadingTimeoutId);
                    setLoading(false);
                    setShowLoading(false);
                }
            }
        };

        // Debounce search
        searchTimeoutId = setTimeout(() => {
            fetchOptions();
        }, inputValue ? 400 : 0); // Increased debounce to 400ms for typing

        return () => {
            active = false;
            if (searchTimeoutId) {
                clearTimeout(searchTimeoutId);
            }
            if (loadingTimeoutId) {
                clearTimeout(loadingTimeoutId);
            }
            setShowLoading(false);
        };
    }, [inputValue, open, apiUrl, lastFetchedSearch, params]);

    const handleChange = (event, newValue) => {
        setSelectedOption(newValue);
        if (newValue) {
            onChange(getOptionValue(newValue), newValue);
        } else {
            onChange(null, null);
        }
    };

    const handleInputChange = (event, newInputValue) => {
        setInputValue(newInputValue);
    };

    const handleClose = () => {
        setOpen(false);
        // Reset search tracking to load fresh data next time
        setLastFetchedSearch('');
        // Keep inputValue if there's a selected option (for display), otherwise clear
        if (!selectedOption) {
            setInputValue('');
        }
    };

    const handleOpen = () => {
        setOpen(true);
        // Reset search tracking when opening to ensure fresh data
        setLastFetchedSearch('');
        // Clear options to force fresh fetch
        setOptions([]);
    };

    // Note: rely on MUI's default label behavior; we only ensure RTL direction via props/styles.

    return (
        <AutocompleteWrapper>
            <Autocomplete
                fullWidth
                open={open}
                onOpen={handleOpen}
                onClose={handleClose}
                value={selectedOption}
                onChange={handleChange}
                inputValue={inputValue}
                onInputChange={handleInputChange}
                options={options}
                loading={loading}
                disabled={disabled}
                filterOptions={(x) => x} // Disable client-side filtering since we do server-side search
                getOptionLabel={(option) => {
                    if (!option) return '';
                    return getOptionLabel(option);
                }}
                getOptionKey={(option) => {
                    // Use a unique identifier (profile id or user id) to avoid duplicate key warnings
                    return option?.id || option?.user?.id || getOptionValue(option) || Math.random();
                }}
                isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    return getOptionValue(option) === getOptionValue(value);
                }}
                noOptionsText="موردی یافت نشد"
                loadingText="در حال جستجو..."
                sx={{
                    direction: 'rtl',
                    '& .MuiAutocomplete-popper': {
                        direction: 'rtl',
                    },
                    // Always show clear (x) button when there is a value or input text
                    '& .MuiAutocomplete-clearIndicator': {
                        opacity: (selectedOption || inputValue) ? 1 : 0,
                        visibility: (selectedOption || inputValue) ? 'visible' : 'hidden',
                        transition: 'opacity 150ms ease-in-out',
                    },
                    // Hide dropdown (popup) icon when field is filled or has text
                    '& .MuiAutocomplete-popupIndicator': {
                        display: (selectedOption || inputValue) ? 'none' : 'flex',
                    },
                }}
                ListboxProps={{
                    sx: {
                        direction: 'rtl',
                        textAlign: 'right',
                    },
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        fullWidth
                        variant="outlined"
                        label={label}
                        placeholder={placeholder}
                        required={required}
                        error={!!error}
                        helperText={error}
                        size={size}
                        inputProps={{
                            ...params.inputProps,
                            dir: 'rtl',
                            style: {
                                ...(params.inputProps?.style || {}),
                                textAlign: 'right',
                            },
                        }}
                        InputLabelProps={{
                            ...params.InputLabelProps,
                            dir: 'rtl',
                            shrink: true,
                        }}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {showLoading ? <CircularProgress size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                        sx={{
                            '& .MuiInputBase-root': {
                                direction: 'rtl',
                            },
                            '& .MuiInputBase-input, & .MuiOutlinedInput-input, & .MuiAutocomplete-input': {
                                textAlign: 'right',
                                direction: 'rtl',
                            },
                            ...additionalSx,
                        }}
                    />
                )}
            />
        </AutocompleteWrapper>
    );
};

SearchableAutocomplete.propTypes = {
    apiUrl: PropTypes.string.isRequired,
    getOptionLabel: PropTypes.func.isRequired,
    getOptionValue: PropTypes.func.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.any,
    onChange: PropTypes.func.isRequired,
    params: PropTypes.object,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    required: PropTypes.bool,
    error: PropTypes.string,
    size: PropTypes.oneOf(['small', 'medium']),
    sx: PropTypes.object,
};

export default SearchableAutocomplete;

